import { inject, Injectable } from '@angular/core';
import { PlayExecutionInteractionErrorDataService } from './play-execution-interaction-error-data.service';
import { PlayDatabaseInteraction, PlayError, PlaySalesforceInteraction } from '@r1-compass-library/models';
import {
    DatabaseErrorTableRow,
    ProcessingErrorTableRow,
    SalesforceErrorTableRow,
} from '@r1-compass-library/components/reporting/interaction-error-table/interaction-error-table.component';
import { PlayExecutionTableRecord } from '@r1-compass-library/components/reporting/report-table/play-execution-table-record.model';
import { map, Observable } from 'rxjs';

/**
 * A service for constructing table rows for the play-execution error/interactions table (embedded in the expanded row of the high-level execution data table)
 */
@Injectable({
    providedIn: 'root',
})
export class PlayExecutionInteractionErrorTableService {
    readonly #playExecutionInteractionErrorDataService = inject(PlayExecutionInteractionErrorDataService);

    getInteractionErrorTableData(
        playResultId: number,
        executionReportData: PlayExecutionTableRecord[]
    ): Observable<PlayExecutionInteractionErrorTableData> {
        return this.#playExecutionInteractionErrorDataService.getErrorsAndInteractions(playResultId).pipe(
            map(res => {
                const tableData = {
                    databaseErrorTableData: this.mapDatabaseInteractions(
                        res.databaseInteractions,
                        res.processingErrors
                    ),
                    processingErrorTableData: this.mapProcessingErrors(res.processingErrors, executionReportData),
                    salesforceErrorTableData: this.mapSalesforceInteractions(
                        res.salesforceInteractions,
                        res.processingErrors
                    ),
                };
                return tableData;
            })
        );
    }

    private mapDatabaseInteractions(
        interactions: PlayDatabaseInteraction[],
        errors: PlayError[]
    ): DatabaseErrorTableRow[] {
        const errorData = errors
            .filter(error => error.errorType === 'database')
            .map(error => {
                const parentInteraction = interactions.find(
                    interaction => interaction.id === error.playDatabaseInteractionId
                );
                return {
                    numErroredRecords: parentInteraction?.numRecordsErrored?.toString() || 'n/a',
                    query: parentInteraction?.query,
                    status: parentInteraction?.status,
                    rawException: error.error,
                };
            });
        const consolidatedErrors: DatabaseErrorTableRow[] = [];
        for (const error of errorData) {
            if (!consolidatedErrors.find(errorObj => errorObj.rawException === error.rawException)) {
                consolidatedErrors.push(error);
            }
        }
        return consolidatedErrors;
    }

    private mapProcessingErrors(
        errors: PlayError[],
        executionReportData: PlayExecutionTableRecord[]
    ): ProcessingErrorTableRow[] {
        const errorData = errors
            .filter(error => error.errorType === 'processing')
            .map(error => {
                return {
                    numErroredRecords:
                        executionReportData
                            .filter(record => record.playResultId === error.playResultId)[0]
                            ?.erroredRecords.toString() ?? 'n/a',
                    rawException: error.error,
                };
            });
        const consolidatedErrors: ProcessingErrorTableRow[] = [];
        for (const error of errorData) {
            if (!consolidatedErrors.find(errorObj => errorObj.rawException === error.rawException)) {
                consolidatedErrors.push(error);
            }
        }
        return consolidatedErrors;
    }

    private mapSalesforceInteractions(
        interactions: PlaySalesforceInteraction[],
        errors: PlayError[]
    ): SalesforceErrorTableRow[] {
        const errorData = errors
            .filter(error => error.errorType === 'salesforce')
            .map(error => {
                const parentInteraction = interactions.find(
                    interaction => interaction.id === error.playSalesforceInteractionId
                );
                return {
                    numErroredRecords: parentInteraction?.numRecordsErrored?.toString() || 'n/a',
                    statusCode: parentInteraction?.statusCode || 'n/a',
                    rawException: error.error,
                };
            });

        const consolidatedErrors: SalesforceErrorTableRow[] = [];
        for (const error of errorData) {
            const existingError = consolidatedErrors.find(errorObj => errorObj.rawException === error.rawException);
            if (!existingError) {
                consolidatedErrors.push({
                    ...error,
                    numErroredRecords: error.numErroredRecords == 'n/a' ? '1' : error.numErroredRecords,
                });
            } else {
                if (error.numErroredRecords === 'n/a') {
                    existingError.numErroredRecords = (parseInt(existingError.numErroredRecords) + 1).toString();
                }
            }
        }

        return [...consolidatedErrors];
    }
}

export type PlayExecutionInteractionErrorTableData = {
    databaseErrorTableData: DatabaseErrorTableRow[];
    processingErrorTableData: ProcessingErrorTableRow[];
    salesforceErrorTableData: SalesforceErrorTableRow[];
};
